// Endpoint definitions injected in contractsApi.js; DTO → VM runs in transformResponse.
import { TAG_TYPES, linkedShipmentsTag } from '@/lib/api/tags';
import { processServerError, handledError } from '@/lib/api/listQuery';
import { buildContractFiltersInput, buildShipmentFiltersInput } from '@/lib/mappers';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import {
  buildContractsQuery,
  buildContractFormData,
  processContractsPage,
  processContractFilterOptions,
  processContractForm,
  processContractDetail,
  processContractDiscrepancy,
  processLinkedShipments,
} from '../mappers/contractsMappers';
import {
  processContractForReceiveShipment,
  buildReceiveShipmentFormData,
} from '../mappers/receiveShipmentMappers';

// The fields the Contracts table renders; `Contract` has 40+.
export const CONTRACTS_QUERY = `
  query Contracts($limit: Int, $page: Int, $orderBy: [ContractOrderBy!], $search: ContractSearch, $filters: ContractFilters) {
    contracts(limit: $limit, page: $page, orderBy: $orderBy, search: $search, filters: $filters) {
      nodes {
        id
        contractId
        status
        material { name displayUnits { unitName convertedUnitName } }
        supplierAccount { companyname }
        site { siteName }
        terminal
        linkedShipmentCount
        workingUnit
        declarationUnit
        workingQuantity
        shippedWorkingQuantity
        outstandingWorkingQuantity
        declarationQuantity
        shippedDeclarationQuantity
        outstandingDeclarationQuantity
        ghgEmissions
        ghgEmissionSavings
        rawMaterialType
        countryOfOrigin
        feedstockCategory
        preferredAllocation
        deliverBy
        discrepancyCount
      }
      pageInfo {
        total
        page
        limit
      }
    }
  }
`;

// The filter panel's Material and Supplier choices — only what the table's contracts carry.
export const CONTRACT_FILTER_OPTIONS_QUERY = `
  query ContractFilterOptions($filters: ContractFilters) {
    contractFilterOptions(filters: $filters) {
      materials { id name }
      suppliers { id name }
    }
  }
`;

// TODO: declarationQuantity — its resolver throws, so the form derives it.
export const CONTRACT_QUERY = `
  query Contract($id: ID!) {
    contract(id: $id) {
      id
      contractId
      site { id }
      terminalRefId
      backToBackAccountId
      supplierAccount { id }
      material { id }
      deliverBy
      transport
      vessel
      contractedQuantity
      contractedQuantityUnit
      conversionFactor
      workingUnitStatus
      workingUnitMessage
      linkedShipments {
        id
        batchGroup {
          id
          shipmentId
        }
      }
      ghgEmissions
      ghgEmissionSavings
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
      singleCounting
      attachments { file { id filename } }
    }
  }
`;

// Contract Details — Overview tab, meta bar, both discrepancy modals and Attachments.
const DISCREPANCY_ITEM_FIELDS = `
  fragment DiscrepancyItemFields on DiscrepancyItem {
    type
    message
    attribute
    contractValue
    shipmentValue
    declarationValue
    shipmentId
    declarationId
    deliverBy
    daysLeft
    outstandingQty
  }
`;

export const CONTRACT_DETAIL_QUERY = `
  ${DISCREPANCY_ITEM_FIELDS}
  query ContractDetail($id: ID!) {
    contract(id: $id) {
      id
      contractId
      material { name displayUnits { unitName convertedUnitName } }
      supplierAccount { companyname }
      site { siteName }
      terminal
      transport
      vessel
      deliverBy
      status
      linkedShipmentCount
      workingUnit
      workingUnitStatus
      workingUnitMessage
      declarationUnit
      workingQuantity
      shippedWorkingQuantity
      outstandingWorkingQuantity
      declarationQuantity
      shippedDeclarationQuantity
      outstandingDeclarationQuantity
      ghgEmissions
      ghgEmissionSavings
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
      discrepancyCount
      discrepancies {
        count
        items { ...DiscrepancyItemFields }
      }
      attachments {
        id
        attachmentType
        file { id filename updatedAt }
      }
    }
  }
`;

// The discrepancy modal only — the detail query pulls 30+ fields this popup never shows.
export const CONTRACT_DISCREPANCY_QUERY = `
  ${DISCREPANCY_ITEM_FIELDS}
  query ContractDiscrepancy($id: ID!) {
    contract(id: $id) {
      id
      contractId
      status
      terminal
      deliverBy
      material { name displayUnits { unitName convertedUnitName } }
      supplierAccount { companyname }
      site { siteName }
      workingUnit
      workingQuantity
      shippedWorkingQuantity
      outstandingWorkingQuantity
      discrepancies {
        items { ...DiscrepancyItemFields }
      }
    }
  }
`;

// Linked Incoming Shipments tab; `limit: 100` is the server's max, so one page holds all.
export const LINKED_SHIPMENTS_FOR_CONTRACT_QUERY = `
  query LinkedShipmentsForContract($contractId: ID!, $filters: ShipmentFilters) {
    linkedShipmentsForContract(contractId: $contractId, limit: 100, page: 1, filters: $filters) {
      nodes {
        linkId
        assignedQuantityWorking
        assignedQuantityDeclaration
        linkedContractIds
        shipment {
          id
          shipmentId
          terminal
          status
          workingUnit
          declarationUnit
          shipmentQuantityWorking
          shipmentQuantityDeclaration
          unassignedContractQuantityWorking
          unassignedContractQuantityDeclaration
          pendingShipmentQuantityWorking
          pendingShipmentQuantityDeclaration
          availableShipmentQuantityWorking
          availableShipmentQuantityDeclaration
          ghgEmissions
          ghgEmissionSavings
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          dateOfDispatch
          discrepancyCount
          discrepancies { type message }
        }
      }
    }
  }
`;

// Receive shipment ▸ the contract bar and every field the new shipment inherits.
export const CONTRACT_RECEIVE_SHIPMENT_QUERY = `
  query ContractForReceiveShipment($id: ID!) {
    contract(id: $id) {
      id
      contractId
      status
      material { id name displayUnits { unitName convertedUnitName convertedWeightQuantity } }
      site { id siteName }
      backToBackAccount { id accountName }
      terminal
      terminalRefId
      supplierAccount { id companyname }
      transport
      vessel
      workingUnit
      declarationUnit
      contractedQuantity
      contractedQuantityUnit
      conversionFactor
      ghgEmissions
      ghgEmissionSavings
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
    }
  }
`;

// Reads are GraphQL, writes REST — see slices/contracts/CLAUDE.md.
const PATHS = {
  contracts: (module) => `/${module}/contracts`,
  contract: (module, id) => `/${module}/contracts/${id}`,
  contractStatus: (module, id) => `/${module}/contracts/${id}/status`,
  receiveShipment: (module, id) => `/${module}/contracts/${id}/receive-shipment`,
  contractShipment: (module, id, shipmentId) =>
    `/${module}/contracts/${id}/shipments/${shipmentId}`,
};

export const contractsEndpoints = (build) => ({
  getContracts: build.query({
    query: (screenState) => ({
      document: CONTRACTS_QUERY,
      variables: buildContractsQuery(screenState),
    }),
    transformResponse: (data) => processContractsPage(data.contracts),
    providesTags: (result) => [
      ...(result?.rows ?? []).map((row) => ({ type: TAG_TYPES.Contract, id: row.id })),
      { type: TAG_TYPES.Contract, id: 'LIST' },
    ],
  }),

  // Takes the page's fixed scope only — a picked filter here would hide its siblings.
  getContractFilterOptions: build.query({
    query: (extraFilters) => ({
      document: CONTRACT_FILTER_OPTIONS_QUERY,
      variables: { filters: buildContractFiltersInput({}, extraFilters) },
    }),
    transformResponse: (data) => processContractFilterOptions(data.contractFilterOptions),
    providesTags: [{ type: TAG_TYPES.Contract, id: 'LIST' }],
  }),

  getContractForm: build.query({
    query: (id) => ({ document: CONTRACT_QUERY, variables: { id } }),
    transformResponse: (data) => processContractForm(data.contract),
    transformErrorResponse: handledError,
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Contract, id }],
  }),

  // Just the popup's own fields, so a badge click doesn't pay for the whole detail read.
  getContractDiscrepancy: build.query({
    query: (id) => ({ document: CONTRACT_DISCREPANCY_QUERY, variables: { id } }),
    transformResponse: (data) => processContractDiscrepancy(data.contract),
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Contract, id }],
  }),

  // Contract Details page — Overview tab, meta bar, header discrepancy modal, Attachments tab.
  getContractDetail: build.query({
    query: (id) => ({ document: CONTRACT_DETAIL_QUERY, variables: { id } }),
    transformResponse: (data) => processContractDetail(data.contract),
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Contract, id }],
  }),

  // Linked Incoming Shipments tab; `contractBusinessId`/`workingUnit` reuse getContractDetail's data.
  getLinkedShipments: build.query({
    query: ({ id, filters }) => ({
      document: LINKED_SHIPMENTS_FOR_CONTRACT_QUERY,
      variables: { contractId: id, filters: buildShipmentFiltersInput(filters) },
    }),
    transformResponse: (data, meta, arg) =>
      processLinkedShipments(data.linkedShipmentsForContract, {
        contractId: arg.contractBusinessId,
        workingUnit: arg.workingUnit,
        declarationUnit: arg.declarationUnit,
      }),
    providesTags: (result, error, arg) => [linkedShipmentsTag(arg.id)],
  }),

  getContractForReceiveShipment: build.query({
    query: (id) => ({ document: CONTRACT_RECEIVE_SHIPMENT_QUERY, variables: { id } }),
    transformResponse: (data) => processContractForReceiveShipment(data.contract),
    transformErrorResponse: handledError,
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Contract, id }],
  }),

  // Creates a TO_RECEIVED shipment against the contract; multipart, so REST.
  receiveShipmentForContract: build.mutation({
    query: ({ module, id, values }) => ({
      url: PATHS.receiveShipment(module, id),
      method: 'POST',
      body: buildReceiveShipmentFormData(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('RECEIVE_SHIPMENT_SAVED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Contract, id: arg.id },
      { type: TAG_TYPES.Contract, id: 'LIST' },
      { type: TAG_TYPES.Shipment, id: 'LIST' },
      linkedShipmentsTag(arg.id),
    ],
  }),

  createContract: build.mutation({
    query: ({ module, values, equivalentUnit }) => ({
      url: PATHS.contracts(module),
      method: 'POST',
      body: buildContractFormData(values, { equivalentUnit }),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CONTRACTS_SAVED'),
    invalidatesTags: [{ type: TAG_TYPES.Contract, id: 'LIST' }],
  }),

  updateContract: build.mutation({
    query: ({ module, id, values, equivalentUnit }) => ({
      url: PATHS.contract(module, id),
      method: 'PATCH',
      body: buildContractFormData(values, { equivalentUnit }),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CONTRACTS_SAVED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Contract, id: arg.id },
      { type: TAG_TYPES.Contract, id: 'LIST' },
    ],
  }),

  // Rejected server-side for a closed contract or one with linked shipments — see contractActions.js.
  deleteContract: build.mutation({
    query: ({ module, id }) => ({
      url: PATHS.contract(module, id),
      method: 'DELETE',
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CONTRACTS_DELETED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Contract, id: arg.id },
      { type: TAG_TYPES.Contract, id: 'LIST' },
    ],
  }),

  // Mark as closed / unmark: the one toggle route, `status` being ACTIVE or CLOSED.
  updateContractStatus: build.mutation({
    query: ({ module, id, status }) => ({
      url: PATHS.contractStatus(module, id),
      method: 'PATCH',
      body: { status },
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CONTRACTS_STATUS_UPDATED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Contract, id: arg.id },
      { type: TAG_TYPES.Contract, id: 'LIST' },
    ],
  }),

  // No unlink mutation exists in the schema — this is REST-only.
  unlinkContractShipment: build.mutation({
    query: ({ module, id, shipmentId }) => ({
      url: PATHS.contractShipment(module, id, shipmentId),
      method: 'DELETE',
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CONTRACTS_LINKED_SHIPMENTS_REMOVED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Contract, id: arg.id },
      linkedShipmentsTag(arg.id),
    ],
  }),
});
